import React, { useEffect, useState } from "react";
import ReactModal from "react-modal";
import { Button, Icon, IconButton } from "../../components/ui";

/**
 * Confirmation dialog for "Remove payment".
 *
 * Removing a payment ONLY updates the order (balance + Xero invoice) — it does
 * NOT move any money. So the rep must explicitly confirm the funds were already
 * refunded from the bank / card out-of-band before we let the action through:
 * a required checkbox gates the destructive button.
 *
 * Styling is fully INLINE (RC OS v3 dark theme tokens) on purpose: a separate
 * .scss file was not being applied to this portal-rendered modal, leaving it
 * unstyled/full-width. Inline styles are deterministic and always win, so the
 * dialog reliably renders as a small, centred, dark rectangle like the app's
 * other pop-ups.
 *
 * `onConfirm` returns a promise — it resolves on success (we close) or rejects
 * with an axios error whose `response.data.message` we surface inline (e.g. the
 * 502 Xero reversal message). The DELETE is sent `metadata.silent` upstream so
 * this modal is the single place that message is shown.
 */

// RC OS v3 dark theme tokens (mirrored from SendPaymentLinkModal.scss).
const C = {
  bg: "#141414",
  panel: "#1a1a1a",
  headline: "#ffffff",
  text: "#E4E4E4",
  para: "#B7B7B7",
  muted: "#797979",
  outline: "#303030",
  red: "#ED6A5F",
  purple: "#7145D0",
  font: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
};

const S = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0, 0, 0, 0.7)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "16px",
    zIndex: 1000,
  },
  content: {
    // The box itself — inline so it can't be overridden. Small, centred.
    position: "relative",
    inset: "auto",
    width: "100%",
    maxWidth: "420px",
    background: C.bg,
    border: "none",
    borderRadius: "16px",
    padding: "22px",
    color: C.text,
    fontFamily: C.font,
    maxHeight: "90vh",
    overflow: "auto",
    boxSizing: "border-box",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "12px",
  },
  h2: {
    fontFamily: C.font,
    fontSize: "18px",
    fontWeight: 600,
    color: C.headline,
    margin: 0,
  },
  body: {
    color: C.para,
    fontSize: "14px",
    lineHeight: 1.5,
    margin: "0 0 16px",
  },
  confirm: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    background: C.panel,
    border: `1px solid ${C.outline}`,
    borderRadius: "8px",
    padding: "12px 14px",
    margin: "0 0 16px",
    cursor: "pointer",
    color: C.text,
    fontSize: "14px",
  },
  checkbox: {
    marginTop: "2px",
    width: "16px",
    height: "16px",
    accentColor: C.purple,
    cursor: "pointer",
    flexShrink: 0,
  },
  error: {
    background: "rgba(237, 106, 95, 0.1)",
    border: `1px solid ${C.red}`,
    color: C.red,
    borderRadius: "8px",
    padding: "10px 12px",
    fontSize: "13px",
    marginBottom: "16px",
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "8px",
    marginTop: "8px",
  },
};

export function RemovePaymentModal({ open, onClose, onConfirm }) {
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Reset every time the dialog is (re)opened so a previous error / tick
  // doesn't leak into the next removal.
  useEffect(() => {
    if (open) {
      setConfirmed(false);
      setSubmitting(false);
      setError(null);
    }
  }, [open]);

  const handleConfirm = async () => {
    if (!confirmed || submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      await onConfirm();
      onClose();
    } catch (e) {
      setError(
        e?.response?.data?.message ||
          "The payment could not be removed. Please try again."
      );
      setSubmitting(false);
    }
  };

  return (
    <ReactModal
      isOpen={open}
      // Don't allow a backdrop / ESC close mid-request.
      onRequestClose={submitting ? undefined : onClose}
      ariaHideApp={false}
      style={{ overlay: S.overlay, content: S.content }}
    >
      <div style={S.header}>
        <h2 style={S.h2}>Remove payment</h2>
        {!submitting && (
          <IconButton label="Close" variant="ghost" size="sm" onClick={onClose}>
            <Icon name="X" size="sm" />
          </IconButton>
        )}
      </div>

      <p style={S.body}>
        This will remove the payment from the order and update the balance and
        Xero invoice. It does <strong style={{ color: C.headline }}>NOT</strong>{" "}
        refund any money. Confirm that this payment has already been refunded
        from the bank account / card before continuing.
      </p>

      <label style={S.confirm}>
        <input
          type="checkbox"
          style={S.checkbox}
          checked={confirmed}
          onChange={(e) => setConfirmed(e.target.checked)}
          disabled={submitting}
        />
        <span>I confirm this payment has been refunded from the bank account.</span>
      </label>

      {error && <div style={S.error}>{error}</div>}

      <div style={S.actions}>
        <Button
          variant="secondary"
          data-btn="payment-cancel-remove"
          onClick={onClose}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          variant="danger"
          data-btn="payment-delete-confirm"
          onClick={handleConfirm}
          disabled={!confirmed}
          loading={submitting}
        >
          Remove payment
        </Button>
      </div>
    </ReactModal>
  );
}

export default RemovePaymentModal;
